import { createInstance, dateReviver } from '@/api';
import {
    CreateProcessTriggerDto,
    ProcessTriggerClient,
    ProcessTriggerDetailDto,
    ProcessTriggerStatusDto,
    UpdateProcessTriggerDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useProcessTriggerEditStore } from './process-trigger-edit.store';

export interface ProcessTriggerDetailStoreState {
    detail?: ProcessTriggerDetailDto;
    loading: boolean;
    reloading: boolean;
    cronDescription: string;
    status?: ProcessTriggerStatusDto;
}

export const client = new ProcessTriggerClient('', createInstance(), dateReviver);

const statusAfterExecuteDelayMs = 200;

export const useProcessTriggerDetailStore = defineStore('configuration-process-trigger-detail-store', {
    state: (): ProcessTriggerDetailStoreState => ({
        detail: null,
        loading: false,
        reloading: false,
        cronDescription: null,
        status: null
    }),
    getters: {},
    actions: {
        async get(id: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            try {
                this.detail = await client.getDetail(id);
                return this.detail;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('processtrigger.detail.error.get.title'), error, false);
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async getDescription(cronSchedule: string) {
            if (!cronSchedule) {
                return;
            }
            try {
                this.cronDescription = await client.getDescription(cronSchedule);
                return this.cronDescription;
            } catch (error) {
                console.error('Error getting cron description', error);
                return '';
            }
        },
        async create(dto: CreateProcessTriggerDto): Promise<number> {
            this.loading = true;
            let response = null;
            try {
                response = await client.create(dto);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('processtrigger.create.error.title'), error, false);
            } finally {
                useProcessTriggerEditStore().closeEdit();
                this.loading = false;
            }
            return response?.id;
        },
        async update(id: number, dto: UpdateProcessTriggerDto) {
            try {
                this.reloading = true;
                await client.update(id, dto);
                useAlertStore().setSuccess(i18n.global.t('processtrigger.detail.success.update.title'));
                await this.get(id, true);
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('processtrigger.detail.error.update.title'),
                    error,
                    false
                );
            } finally {
                useProcessTriggerEditStore().closeEdit();
                this.reloading = false;
            }
        },
        async delete(id: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('processtrigger.detail.success.delete.title'));
                return true;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('processtrigger.detail.error.delete.title'),
                    error,
                    false
                );
            } finally {
                useProcessTriggerEditStore().closeEdit();
                this.reloading = false;
            }
            return false;
        },
        async execute(id: number): Promise<boolean> {
            this.reloading = true;
            try {
                useAlertStore().clearAlert();
                await client.execute(id);
                useAlertStore().setSuccess(i18n.global.t('processtrigger.detail.success.execute.title'));
                await this.getStatus(id, statusAfterExecuteDelayMs);
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('processtrigger.detail.error.execute.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        // Polled every few seconds while the detail screen is open, so a hiccup must not throw an alert in
        // the user's face; the next tick will tell us soon enough. A caller that has just started the
        // trigger can ask to hold off for a moment first: the quartz job starts on a scheduler thread
        // shortly after that call returns, so asking straight away tends to be a shade too early.
        async getStatus(id: number, waitTimeMs = 0) {
            try {
                if (waitTimeMs > 0) {
                    await new Promise((resolve) => setTimeout(resolve, waitTimeMs));
                }

                this.status = await client.getStatus(id);
            } catch (error) {
                console.error('Error getting process trigger status', error);
            }
            return this.status;
        },
        clear(id: number) {
            this.detail = null;
            this.cronDescription = null;
            this.status = null;
        }
    }
});
